import Store from '@ember-data/store';
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { type SafeString, htmlSafe } from '@ember/template';
import { isPresent } from '@ember/utils';
import { cached, tracked } from '@glimmer/tracking';
import type { AssignToCampaignSavedArgs } from 'donor-elf-web/components/line-item/bulk-edit/assign-to-campaign-modal';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import {
  type AppliedPropertyFilter,
  ContactIdsIntersection,
  type Operation,
  type PropertyFilterInfoForQueryFilter,
  type PropertyType,
  deserializeAppliedFilters,
  serializeAppliedFilters,
} from 'donor-elf-web/lib/filter-utils-new';
import objectUtils from 'donor-elf-web/lib/object-utils';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import LineItemModel from 'donor-elf-web/models/line-item';
import type { TransactionRouteModel } from 'donor-elf-web/routes/funds/fund/transactions';
import CampaignService from 'donor-elf-web/services/campaign-service';
import ContactsCache from 'donor-elf-web/services/contacts-cache';
import ContactsExportService from 'donor-elf-web/services/contacts-export';
import CurrentUser from 'donor-elf-web/services/current-user';
import FormatDateService from 'donor-elf-web/services/format-date-service';
import FundsService from 'donor-elf-web/services/funds-service';
import FileSaver from 'file-saver';
import moment from 'moment';
import Papa from 'papaparse';

const PAGE_SIZE = 50;

class TableRow {
  constructor(lineItem: LineItemModel) {
    this.lineItem = lineItem;
  }

  @tracked selected = false;
  lineItem!: LineItemModel;
}

export default class TransactionsController extends Controller {
  @service declare campaignService: CampaignService;
  @service declare contactsCache: ContactsCache;
  @service declare contactsExport: ContactsExportService;
  @service declare currentUser: CurrentUser;
  @service declare formatDateService: FormatDateService;
  @service declare fundsService: FundsService;
  @service declare store: Store;

  @tracked model!: TransactionRouteModel;

  queryParams = ['page', 'filters'];

  @tracked filters?: string;
  @tracked appliedFilters?: AppliedPropertyFilter[];
  @tracked loading = true;
  @tracked page = 1;
  @tracked downloading = false;
  @tracked percentageDownloadedStyle?: SafeString;
  @tracked downloadComplete = false;

  @tracked rows: TableRow[] = [];
  @tracked totalPages = 0;
  @tracked totalAmount = 0;
  @tracked monthlyAverage = 0;

  @tracked showAssignToCampaignModal = false;

  @action
  templateDidInsert() {
    this.deserializeFilters();
    this.load();
  }

  @action
  templateWillDestroy() {
    this.loading = true;
    this.filters = undefined;
    this.appliedFilters = undefined;
    this.page = 1;
    this.downloading = false;
    this.downloadComplete = false;
    this.rows = [];
    this.showAssignToCampaignModal = false;
  }

  async load() {
    this.loading = true;
    let queryFilter: any = {
      offset: (this.page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    };
    let { availablePropertyFilters } = this;
    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      await propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }

    let records = await this.store.query('line-item', {
      filter: objectUtils.decamelizeKeys(queryFilter),
    }) as any;

    let totalCount = Number(records.meta.total_count || 0);
    this.totalPages = Math.ceil(totalCount / PAGE_SIZE);
    this.totalAmount = Number(records.meta.total_amount);
    let monthlyAverage = 0;
    let { timeFrameMonths } = this;
    if (this.timeFrameMonths > 0) {
      monthlyAverage = (this.totalAmount || 0) / timeFrameMonths;
    }
    this.monthlyAverage = monthlyAverage;
    let rows: TableRow[] = [];
    for (let lineItem of records.slice()) {
      rows.push(new TableRow(lineItem));
    }
    this.rows = rows;
    this.loading = false;
  }

  @cached
  get availablePropertyFilters(): PropertyFilterInfoForQueryFilter[] {
    let list: PropertyFilterInfoForQueryFilter[] = [];
    list.push({
      propertyName: 'transDate',
      label: 'Date',
      propertyType: 'date',
      availableOperations: ['><', '>=', '<=', '='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        if (appliedFilter.operation === '>=') {
          queryFilter.from = appliedFilter.val;
        } else if (appliedFilter.operation === '<=') {
          queryFilter.to = appliedFilter.val;
        } else if (appliedFilter.operation === '><') {
          let dates = appliedFilter.val as string[];
          queryFilter.from = dates[0];
          queryFilter.to = dates[1];
        } else {
          queryFilter.date = appliedFilter.val;
        }
      },
    });
    list.push({
      propertyName: 'contactIds',
      label: 'Contact',
      propertyType: 'contactIds',
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.contactIds = appliedFilter.val;
      },
    });
    if ((this.model.transTypes || []).length > 0) {
      list.push({
        propertyName: 'type',
        label: 'Type',
        propertyType: 'list',
        availableOperations: ['any'],
        listOptions: this.model.transTypes,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          queryFilter.transType = appliedFilter.val;
        },
      });
    }
    list.push({
      propertyName: 'income',
      label: 'Income Only',
      propertyType: 'no-operation',
      applyToQueryFilter: async (
        _appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.incomeOrExpense = 'income';
      },
    });
    list.push({
      propertyName: 'expense',
      label: 'Expense Only',
      propertyType: 'no-operation',
      applyToQueryFilter: async (
        _appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.incomeOrExpense = 'expense';
      },
    });
    list.push({
      propertyName: 'amount',
      label: 'Amount',
      propertyType: 'amount',
      availableOperations: ['=', '>', '>=', '<', '<='],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.amountNumber = appliedFilter.val;
        queryFilter.amountOperator = appliedFilter.operation;
      },
    });
    if (this.campaignService.campaignsEnabled) {
      list.push({
        propertyName: 'campaignId',
        label: 'Campaign',
        propertyType: 'list',
        availableOperations: ['any', 'absent', 'present'],
        listOptions: sortByProperties(this.campaignService.campaigns, [
          'name',
        ]).map((x) => {
          return { val: x.id, label: x.name };
        }),
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          if (appliedFilter.operation === 'any') {
            queryFilter.campaignId = appliedFilter.val;
          } else if (appliedFilter.operation === 'absent') {
            queryFilter.campaignId = 'absent';
          } else if (appliedFilter.operation === 'present') {
            queryFilter.campaignId = 'present';
          }
        },
      });
    }
    for (let item of this.model.customProperties) {
      if (item.type === 'amount' || item.type === 'number') {
        list.push({
          propertyName: `custom_${item.label}`,
          label: item.label,
          propertyType: item.type,
          availableOperations: ['=', '>', '>=', '<', '<=', 'absent', 'present'],
          applyToQueryFilter: async (
            appliedFilter: AppliedPropertyFilter,
            queryFilter: any,
          ) => {
            queryFilter[`new-custom-${item.label.toLowerCase()}`] = {
              val: appliedFilter.val,
              operation: appliedFilter.operation,
            };
          },
        });
      } else if (item.type === 'string') {
        let propertyType: PropertyType = 'string';
        let availableOperations: Operation[] = [
          '=',
          'stringContains',
          'absent',
          'present',
        ];
        let listOptions: string[] | undefined = undefined;
        if (isPresent(item.distinct_values)) {
          availableOperations = ['=', 'absent', 'present'];
          listOptions = item.distinct_values!;
        }
        list.push({
          propertyName: `custom_${item.label}`,
          label: item.label,
          propertyType,
          availableOperations,
          listOptions,
          applyToQueryFilter: async (
            appliedFilter: AppliedPropertyFilter,
            queryFilter: any,
          ) => {
            queryFilter[`new-custom-${item.label.toLowerCase()}`] = {
              val: appliedFilter.val,
              operation:
                appliedFilter.operation === 'stringContains'
                  ? 'ilike'
                  : appliedFilter.operation,
            };
          },
        });
      }
      // TODO: does not work for 'date' or 'boolean' item types yet
    }
    list.push({
      propertyName: 'memo',
      label: 'Memo',
      propertyType: 'string',
      availableOperations: ['stringContains'],
      applyToQueryFilter: async (
        appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.memoContains = appliedFilter.val;
      },
    });
    if (this.model.hasAnonymous) {
      list.push({
        propertyName: 'anonymous',
        label: 'Anonymous Gifts',
        propertyType: 'no-operation',
        applyToQueryFilter: async (
          _appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          queryFilter.anonymous = true;
        },
      });
    }
    list.push({
      propertyName: 'affectsGoal',
      label: 'Affects the Goal',
      propertyType: 'no-operation',
      applyToQueryFilter: async (
        _appliedFilter: AppliedPropertyFilter,
        queryFilter: any,
      ) => {
        queryFilter.affectsGoal = true;
      },
    });
    let contactFilterInfos =
      this.contactsCache.availablePropertyFiltersForContacts.filter(
        (x) => x.custom.includeOnAnyReport && x.propertyName !== 'campaignIds',
      );
    for (let filterInfo of contactFilterInfos) {
      list.push({
        propertyName: filterInfo.propertyName,
        label: `Contact ${filterInfo.label}`,
        propertyType: filterInfo.propertyType,
        availableOperations: filterInfo.availableOperations,
        listOptions: filterInfo.listOptions,
        listIsSingleSelection: filterInfo.listIsSingleSelection,
        applyToQueryFilter: async (
          appliedFilter: AppliedPropertyFilter,
          queryFilter: any,
        ) => {
          let contactIds = (
            await this.contactsCache.filter([appliedFilter])
          ).map((x) => x.id);
          if (contactIds.length === 0) {
            contactIds = ['-1']; // since no records match the contact filter, force the api to return 0 records
          }
          queryFilter.contactIds = ContactIdsIntersection(
            queryFilter.contactIds,
            contactIds,
          );
        },
      });
    }
    return list;
  }

  get showBulkEdit() {
    return this.campaignService.campaignsEnabled && this.rows.length > 0;
  }

  @action
  toggleAllRowsSelected() {
    let selected = !this.allRowsSelected;
    for (let row of this.rows) {
      row.selected = selected;
    }
  }

  get allRowsSelected() {
    return this.rows.every((x) => x.selected);
  }

  @action
  toggleSelectRow(row: TableRow) {
    row.selected = !row.selected;
  }

  @action
  handleFiltersChanged(currentFilters: AppliedPropertyFilter[]) {
    this.page = 1;
    this.appliedFilters = currentFilters;
    this.serializeFilters();
    this.load();
  }

  serializeFilters() {
    this.filters = serializeAppliedFilters(this.appliedFilters);
  }

  deserializeFilters() {
    this.appliedFilters = deserializeAppliedFilters(this.filters);
  }

  get timeFrameMonths(): number {
    let afterDateFilter = this.appliedFilters?.find(
      (x) => x.propertyName === 'transDate' && x.operation === '>=',
    );
    let beforeDateFilter = this.appliedFilters?.find(
      (x) => x.propertyName === 'transDate' && x.operation === '<=',
    );
    if (afterDateFilter == null || beforeDateFilter == null) {
      return 0;
    }
    let fromMoment = moment(<string>afterDateFilter.val);
    let toMoment = moment(<string>beforeDateFilter.val);
    return (
      toMoment.year() * 12 +
      toMoment.month() +
      1 -
      fromMoment.year() * 12 -
      fromMoment.month()
    );
  }

  get actionsMenuItems() {
    return [
      {
        label: 'Download',
        svgJar: 'download',
        onClick: this.download,
      },
    ];
  }

  @action
  changePage(pageNumber: number) {
    this.page = pageNumber;
    this.load();
  }

  @action
  async download() {
    this.downloading = true;
    this.downloadComplete = false;
    this.percentageDownloadedStyle = htmlSafe('width: 5%');

    let queryFilter: any = {};
    let { availablePropertyFilters } = this;
    for (let appliedFilter of this.appliedFilters || []) {
      let propertyFilter = availablePropertyFilters.find(
        (x) => x.propertyName === appliedFilter.propertyName,
      )!;
      propertyFilter.applyToQueryFilter(appliedFilter, queryFilter);
    }
    // if the filter excludes all contacts so pass in an invalid contact id to get no results
    if (queryFilter.contactId && queryFilter.contactId.length === 0) {
      queryFilter.contactId = ['none'];
    }

    let downloadedLines = await queryWithPages<LineItemModel>({
      store: this.store,
      modelName: 'lineItem',
      queryOptions: { filter: objectUtils.decamelizeKeys(queryFilter) },
      pageSize: PAGE_SIZE,
      pageCallback: (args) => {
        let percentageDownloaded = Math.round(
          (args.downloadedCount * 100) / args.totalCount,
        );
        if (percentageDownloaded < 5) {
          percentageDownloaded = 5;
        }
        this.percentageDownloadedStyle = htmlSafe(
          `width: ${percentageDownloaded}%`,
        );
      },
    });

    let { formatDateService, contactsCache } = this;
    let customLabels = this.getCustomLabelsFromLineItems(downloadedLines);
    let headerRow = ['Date', 'Payee', 'Type', 'Amount', 'Memo'].concat(
      customLabels,
    );
    if (this.campaignService.campaignsEnabled) {
      headerRow.push('Campaign');
    }
    headerRow.push('---');
    // Added this for Jeff Mashaw and Dick Senzig
    let addIdFields =
      this.fundsService.selectedFund?.id === '52699' ||
      this.currentUser.email === 'dsenzig@gmail.com';
    if (addIdFields) {
      headerRow.unshift('Contact Id');
      headerRow.unshift('Transaction Id');
    }
    let headerInfo = await this.contactsExport.addContactsHeaderColumns(
      headerRow,
      contactsCache.contacts,
    );
    let rows: any[] = [headerRow];
    for (let line of downloadedLines) {
      let contact = contactsCache.getById(line.contactId);
      let contactName = contact?.name || '';
      let row = [
        formatDateService.formatForFile(line.transDate),
        contactName,
        line.transType,
        line.amount,
        line.memo,
      ];
      if (addIdFields) {
        row.unshift(line.contactId || '');
        row.unshift(line.id);
      }
      for (let label of customLabels) {
        row.push(line.custom?.[label]);
      }
      if (this.campaignService.campaignsEnabled) {
        let campaignLabel =
          this.campaignService.campaignsById[
            line.campaignLineItemsArray[0]?.campaignId!
          ]?.label;
        row.push(campaignLabel);
      }
      row.push('');
      await this.contactsExport.addContactInfoToRow(row, contact, headerInfo);
      rows.push(row);
    }

    this.store.createRecord('contact-download').save();

    let csv = Papa.unparse(rows);
    var blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'transactions.csv', true);

    this.downloadComplete = true;
  }

  @action
  finishedWithDownload() {
    this.downloading = false;
    this.downloadComplete = false;
  }

  getCustomLabelsFromLineItems(allLines: LineItemModel[]) {
    let customLabels: any = {};
    allLines.forEach((line) => {
      if (line.custom) {
        for (let label in line.custom) {
          customLabels[label] = true;
        }
      }
    });
    return Object.keys(customLabels);
  }

  get selectedLineItems() {
    return this.rows.filter((x) => x.selected).map((x) => x.lineItem);
  }

  @action
  handleShowAssignToCampaign() {
    this.showAssignToCampaignModal = true;
  }

  @action
  handleSavedAssignToCampaign(args: AssignToCampaignSavedArgs) {
    let { rows } = this;
    for (let lineItem of args.reloadedLineItems) {
      let row = rows.find((x) => x.lineItem.id === lineItem.id);
      if (row) {
        row.lineItem = lineItem;
      }
    }
    this.showAssignToCampaignModal = false;
  }

  @action
  handleCloseAssignToCampaign() {
    this.showAssignToCampaignModal = false;
  }
}
